import { useEffect, useState } from "react";
import { CardTile } from "./CardTile";
import { SPHERE_TOOLBAR } from "./constants/sphereToolbar";

const CHECKBOX_LIST_CLASS =
  "mt-1 max-h-40 space-y-1 overflow-y-auto rounded-md border border-slate-600 bg-slate-950 p-2";

const CHECKBOX_LABEL_CLASS =
  "flex cursor-pointer items-center gap-2 rounded px-1 py-0.5 text-sm text-slate-200 hover:bg-slate-900";

function MultiFilterGroup({ legend, options, selected, onToggle }) {
  return (
    <fieldset>
      <legend className="text-xs font-semibold uppercase tracking-wide text-slate-500">{legend}</legend>
      <div className={CHECKBOX_LIST_CLASS}>
        {options.map((option) => {
          const on = selected.includes(option);
          return (
            <label key={option} className={CHECKBOX_LABEL_CLASS}>
              <input
                type="checkbox"
                checked={on}
                onChange={() => onToggle(option)}
                className="rounded border-slate-600 bg-slate-950 text-amber-500 focus:ring-amber-500"
              />
              <span>{option}</span>
            </label>
          );
        })}
      </div>
    </fieldset>
  );
}

function CardFilterFields({
  options,
  selectedTypes,
  onToggleType,
  selectedCosts,
  onToggleCost,
  selectedPacks,
  onTogglePack,
}) {
  return (
    <>
      <MultiFilterGroup
        legend="Type"
        options={options.types}
        selected={selectedTypes}
        onToggle={onToggleType}
      />
      <MultiFilterGroup
        legend="Cost"
        options={options.costs}
        selected={selectedCosts}
        onToggle={onToggleCost}
      />
      <MultiFilterGroup
        legend="Pack"
        options={options.packs}
        selected={selectedPacks}
        onToggle={onTogglePack}
      />
    </>
  );
}

const SEARCH_INPUT_CLASS =
  "min-w-0 flex-1 rounded-md border border-slate-600 bg-slate-950 px-3 py-2 text-sm text-slate-100 focus:border-amber-500 focus:outline-none focus:ring-1 focus:ring-amber-500";

function FilterIcon({ className }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M4 6h16M7 12h10M10 18h4" strokeLinecap="round" />
    </svg>
  );
}

function CardsResultCount({ initialLoading, totalCount, cardsLength, className = "" }) {
  return (
    <p className={`text-sm text-slate-400 ${className}`.trim()}>
      {initialLoading
        ? "Loading…"
        : `${totalCount} card${totalCount === 1 ? "" : "s"} · showing ${cardsLength}`}
    </p>
  );
}
function CardsSearchBar({
  qInput,
  onQInputChange,
  mobileFiltersOpen,
  onToggleMobileFilters,
  filtersActive,
  options,
  selectedTypes,
  onToggleType,
  selectedCosts,
  onToggleCost,
  selectedPacks,
  onTogglePack,
}) {
  return (
    <>
      {mobileFiltersOpen && (
        <button
          type="button"
          className="fixed inset-0 z-[39] bg-slate-950/80 lg:hidden"
          aria-label="Close filters"
          onClick={onToggleMobileFilters}
        />
      )}

      <div
        className="fixed inset-x-0 bottom-0 z-40 border-t border-slate-800 bg-slate-950 shadow-[0_-4px_24px_rgba(0,0,0,0.35)]"
        role="search"
      >
        {mobileFiltersOpen && (
          <div
            id="card-filters-mobile"
            className="absolute bottom-full left-0 right-0 max-h-[min(70vh,28rem)] overflow-y-auto border-t border-slate-700 bg-slate-950 lg:hidden"
          >
            <div className="mx-auto max-w-7xl space-y-4 px-4 py-4">
              <CardFilterFields
                options={options}
                selectedTypes={selectedTypes}
                onToggleType={onToggleType}
                selectedCosts={selectedCosts}
                onToggleCost={onToggleCost}
                selectedPacks={selectedPacks}
                onTogglePack={onTogglePack}
              />
            </div>
          </div>
        )}

        <div className="mx-auto flex max-w-7xl items-stretch gap-2 bg-slate-950 px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
        <label htmlFor="search" className="sr-only">
          Search cards
        </label>
        <input
          id="search"
          type="search"
          value={qInput}
          onChange={(e) => onQInputChange(e.target.value)}
          placeholder="Name, traits, rules text…"
          className={SEARCH_INPUT_CLASS}
        />
        <button
          type="button"
          className={[
            "relative flex shrink-0 items-center justify-center rounded-md border px-3 transition lg:hidden",
            mobileFiltersOpen
              ? "border-amber-500/70 bg-amber-950/40 text-amber-100"
              : "border-slate-600 bg-slate-950 text-slate-300 hover:border-amber-500/60 hover:text-amber-100",
          ].join(" ")}
          aria-expanded={mobileFiltersOpen}
          aria-controls="card-filters-mobile"
          aria-label={mobileFiltersOpen ? "Hide filters" : "Show filters"}
          onClick={onToggleMobileFilters}
        >
          <FilterIcon className="h-5 w-5" />
          {filtersActive && (
            <span className="absolute right-1.5 top-1.5 h-2 w-2 rounded-full bg-amber-500" aria-hidden />
          )}
        </button>
      </div>
    </div>
    </>
  );
}

export function CardsTab({
  options,
  qInput,
  onQInputChange,
  selectedSpheres,
  onToggleSphere,
  selectedTypes,
  onToggleType,
  selectedCosts,
  onToggleCost,
  selectedPacks,
  onTogglePack,
  cards,
  initialLoading,
  loadingMore,
  totalCount,
  totalPages,
  hasMore,
  signedIn,
  qtyByCardId,
  onOpenCard,
  onDeckAdd,
  onDeckRemove,
  scrollSentinelRef,
}) {
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const filtersActive = Boolean(selectedTypes.length || selectedCosts.length || selectedPacks.length);

  // Match sticky sidebar/count offsets to the real sphere row height (wraps on narrow desktop).
  useEffect(() => {
    const el = document.getElementById("cards-sphere-toolbar");
    if (!el) return;

    const syncHeight = () => {
      document.documentElement.style.setProperty("--cards-sphere-toolbar-height", `${el.offsetHeight}px`);
    };

    syncHeight();
    const observer = new ResizeObserver(syncHeight);
    observer.observe(el);
    return () => {
      observer.disconnect();
      document.documentElement.style.removeProperty("--cards-sphere-toolbar-height");
    };
  }, []);

  return (
    <>
      <div
        id="cards-sphere-toolbar"
        className="sticky top-[calc(var(--auth-toolbar-height,3.5rem)+var(--main-tabs-height,2.75rem))] z-30 border-b border-slate-800 bg-slate-950/95 backdrop-blur"
      >
        <div className="mx-auto max-w-7xl px-4 py-3">
          <div
            className="flex flex-nowrap items-stretch justify-between gap-1 sm:flex-wrap sm:justify-start sm:gap-2"
            role="toolbar"
            aria-label="Sphere filters"
          >
            {SPHERE_TOOLBAR.map((s) => {
              const on = selectedSpheres.includes(s.name);
              return (
                <button
                  key={s.name}
                  type="button"
                  title={s.title}
                  aria-label={`${s.title} sphere`}
                  aria-pressed={on}
                  onClick={() => onToggleSphere(s.name)}
                  className={[
                    "flex min-w-0 flex-1 flex-col items-center gap-0.5 rounded-lg border border-slate-700 px-1.5 py-1.5 text-[10px] font-medium leading-tight transition sm:min-w-[4.75rem] sm:flex-none sm:gap-1 sm:px-3 sm:py-2 sm:text-xs",
                    on ? `${s.activeBg} ring-2 ${s.activeRing}` : `bg-slate-900 text-slate-500 ${s.idleHover}`,
                  ].join(" ")}
                >
                  <span className="block h-5 w-5 sm:h-7 sm:w-7">{s.icon("h-full w-full")}</span>
                  <span className="max-w-full truncate sm:max-w-[5.5rem]">{s.name}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 py-6 pb-28">
        <CardsResultCount
          initialLoading={initialLoading}
          totalCount={totalCount}
          cardsLength={cards.length}
          className="mb-4 lg:hidden"
        />

        <div className="lg:grid lg:grid-cols-[16rem_minmax(0,1fr)] lg:gap-x-6 lg:items-start">
          <aside className="cards-filter-sidebar hidden space-y-4 lg:block">
            <CardsResultCount
              initialLoading={initialLoading}
              totalCount={totalCount}
              cardsLength={cards.length}
            />
            <CardFilterFields
              options={options}
              selectedTypes={selectedTypes}
              onToggleType={onToggleType}
              selectedCosts={selectedCosts}
              onToggleCost={onToggleCost}
              selectedPacks={selectedPacks}
              onTogglePack={onTogglePack}
            />
          </aside>

          <main className="min-w-0">
            <div className="grid grid-cols-2 gap-3 sm:gap-4 xl:grid-cols-3">
              {cards.map((card) => (
                <CardTile
                  key={card.id}
                  card={card}
                  onOpen={onOpenCard}
                  signedIn={signedIn}
                  deckQuantity={qtyByCardId[card.id] ?? 0}
                  onDeckAdd={() => onDeckAdd(card.id)}
                  onDeckRemove={() => onDeckRemove(card.id)}
                />
              ))}
            </div>

            {!initialLoading && cards.length === 0 && (
              <p className="mt-8 text-center text-slate-500">No cards match these filters.</p>
            )}

            <div ref={scrollSentinelRef} className="h-8 w-full shrink-0" aria-hidden />

            {loadingMore && <p className="mt-4 text-center text-sm text-slate-500">Loading more…</p>}

            {!initialLoading && !loadingMore && !hasMore && cards.length > 0 && totalPages > 1 && (
              <p className="mt-4 text-center text-xs text-slate-600">End of results</p>
            )}
          </main>
        </div>
      </div>

      <CardsSearchBar
        qInput={qInput}
        onQInputChange={onQInputChange}
        mobileFiltersOpen={mobileFiltersOpen}
        onToggleMobileFilters={() => setMobileFiltersOpen((open) => !open)}
        filtersActive={filtersActive}
        options={options}
        selectedTypes={selectedTypes}
        onToggleType={onToggleType}
        selectedCosts={selectedCosts}
        onToggleCost={onToggleCost}
        selectedPacks={selectedPacks}
        onTogglePack={onTogglePack}
      />
    </>
  );
}
